import React, { useEffect, useState, useRef } from 'react';
import {isPermitted, useFeathers} from '../../app/util';
import { useSelector } from 'react-redux';
import {
  useParams,
  Link,
  useHistory
} from 'react-router-dom';

import Modal from '@mui/material/Modal';
import {
  LoadingSpinner,
  Tile,
  electionTypeWritten,
  ProgressBar,
  ElectionStatusTag,
  CoveragePlanMap,
  DataDetailToolbar,
  OfficesList,
  CircularProgressBar,
  ElectionTimeline,
  VotingDate,
  ModalInner,
  ElectionReminderSchedule,
  AnalyticsPopoverContent
} from '../../components';
import styled from 'styled-components';
import moment from 'moment'
import Typography from '@mui/material/Typography';
import { IconButton, Menu, MenuItem, Button, ListItemIcon, ListItemText, Popover } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { CalendarMonth, Edit, Preview, MoreVert, Delete } from '@mui/icons-material';
import { EditElectionStatusModal } from '../../components/EditElectionStatusModal';
import { EditBallotOverview } from '../../components/EditBallotOverview';
import { AdjustPriorityLevelsModal } from './AdjustPriorityLevelsModal';
import {
  getPrecedentConfig,
  LinkElectionPrecedentModal
} from './LinkElectionPrecedentModal';

const ElectionDetail = () => {
  const feathers = useFeathers();
  const electionKey = useParams().key;
  const history = useHistory();
  const user = useSelector(state => state.user);
  const datesEditPermission = isPermitted(user, ['editor', 'super-admin', 'admin']);
  const editElectionStatusPermission = datesEditPermission;
  const canAdjustPriorityLevels = datesEditPermission;
  const electionDataPreloaded = useSelector(state => state?.elections?.byKey[electionKey]);
  const [ election, setElection ] = useState(electionDataPreloaded);
  const [ electionImplementation, setElectionImplementation ] = useState(null)
  const [ loading, setLoading ] = useState(!Boolean(electionDataPreloaded));
  const [ moreMenuAnchorEl, setMoreMenuAnchorEl ] = useState(null);
  const [ progressMoreMenuAnchorEl, setProgressMoreMenuAnchorEl ] = useState(null);
  const [ openReminderModal, setOpenReminderModal ] = useState(false);
  const canViewDetailedElectionData = isPermitted(user, ['editor', 'super-admin', 'researcher', 'reviewer', 'publisher', 'admin', 'dms-shapefile']);
  const [isEditingElectionStatus, setIsEditingElectionStatus] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [racesPopoverAnchorEl, setRacesPopoverAnchorEl] = useState(null);
  const [candidatesPopoverAnchorEl, setCandidatesPopoverAnchorEl] = useState(null);
  const [editBallotOverviewModalOpen, setEditBallotOverviewModalOpen] = useState(false);
  const [adjustPriorityLevelsModalOpen, setAdjustPriorityLevelsModalOpen] = useState(false);
  const [linkPrecedentModalOpen, setLinkPrecedentModalOpen] = useState(false);
  const [savingBallotOverview, setSavingBallotOverview] = useState(false);
  
  // Check if user has permission to delete (super-admin or editor)
  const deletePermission = isPermitted(user, ['super-admin', 'editor']);
  
  // Check if election has any races or measures
  const hasRacesOrMeasures = (electionImplementation?.aggregatedInformation?.numRaces ?? 0) > 0 || 
                             (electionImplementation?.aggregatedInformation?.numMeasures ?? 0) > 0;
  
  // Only show delete option if user has permission AND election has no races or measures
  const canDeleteElection = deletePermission && !hasRacesOrMeasures;
  const precedentConfig = getPrecedentConfig(election);
  const canUseElectionMigration = Boolean(election) && (
    (election.electionType === 'general' && election.primaryPrecedent) ||
    (['primary-runoff', 'general-runoff'].includes(election.electionType) && election.runoffPrecedent)
  );
  const electionMigrationMenuText = (() => {
    if (election?.electionType === 'general-runoff') {
      return 'Migrate from general';
    }
    if (election?.electionType === 'primary-runoff' || election?.electionType === 'general') {
      return 'Migrate from primary';
    }
    return 'Migrate';
  })();

  const baseUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:3030'
          : 'https://www.branch.vote';
  const prepLink = election && election?.status !== 'live' ? `${baseUrl}/setup?election=${electionKey}` : null;

  const upcomingReminders = election?.reminderSchedule?.filter(reminder => moment.utc(reminder.date).isAfter(moment.utc()));

  const displayName = user?.firstName || user?.name;
  const refreshElectionData = async () => {
    try {
      const [
        electionData,
        eiData
      ] = await Promise.all([
        feathers.getService('elections').get(electionKey, {
          query: { $includeReminderSchedule: true }
        }),
        feathers.getService('election-implementations').get(electionKey)
      ])
      setElection(electionData)
      setElectionImplementation(eiData)
      setLoading(false)
    } catch(err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if(feathers) {
      refreshElectionData()
    }
  }, [ feathers ])

  // Check if setup is incomplete and redirect to setup page
  useEffect(() => {
    if(election && !loading) {
      if(!election.setupComplete) {
        history.push(`/elections/${electionKey}/setup`);
      }
    }
  }, [election, loading, electionKey, history])

  if(loading) {
    return (
      <Wrapper>
        <LoadingTile>
          <LoadingSpinner scale={0.8}/>
        </LoadingTile>
      </Wrapper>
    )
  }

  const handleEditElectionStatusModalClose = (election) => {
    setIsEditingElectionStatus(false);
    if (election) {
      refreshElectionData()
    }
  };

  const handleDeleteElection = async () => {
    try {
      await feathers.getService('elections').remove(electionKey);
      setDeleteDialogOpen(false);
      history.push('/elections');
    } catch(err) {
      console.error('Error deleting election:', err);
      alert('Failed to delete election. Please try again.');
    }
  };

  const handleOpenElectionMigration = async () => {
    setMoreMenuAnchorEl(null);
    try {
      const run = await feathers.getService('election-migration-runs').create({
        targetElectionKey: electionKey
      });
      history.push(`/elections/${electionKey}/election-migration/${run._id}`);
    } catch (err) {
      console.error('Error opening election migration run:', err);
      alert('Failed to open election migration. Please try again.');
    }
  };

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: {
            pathname: `/elections`,
          }}}
        navTree={[
          {
            text: 'Elections',
            to: {
              pathname: `/elections`,
            }
          },
          { text: election?.name }
        ]}
        actionButtonsComponent={
          <>
            <IconButton
              onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
              size="small"
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={() => setMoreMenuAnchorEl(null)}
            >
              <MenuItem 
                component={Link} 
                to={`/elections/${electionKey}/candidate-lists`}
                onClick={() => setMoreMenuAnchorEl(null)}
              >
                Upload candidate lists
              </MenuItem>
              <MenuItem 
                component={Link} 
                to={`/elections/${electionKey}/measure-lists`}
                onClick={() => setMoreMenuAnchorEl(null)}
              >
                Upload measure lists
              </MenuItem>
              <MenuItem
                component={Link}
                to={`/elections/${electionKey}/contest-shapefiles`}
                onClick={() => setMoreMenuAnchorEl(null)}
              >
                Contest shapefiles
              </MenuItem>
              <MenuItem 
                component={Link} 
                to={`/elections/${electionKey}/auto-scouters`}
                onClick={() => setMoreMenuAnchorEl(null)}
              >
                View auto scouters
              </MenuItem>
              {canUseElectionMigration && (
                <MenuItem onClick={handleOpenElectionMigration}>
                  {electionMigrationMenuText}
                </MenuItem>
              )}
              {precedentConfig && (
                <MenuItem
                  onClick={() => {
                    setMoreMenuAnchorEl(null);
                    setLinkPrecedentModalOpen(true);
                  }}
                >
                  Link {precedentConfig.label} election
                </MenuItem>
              )}
              {canDeleteElection && (
                <MenuItem 
                  onClick={() => {
                    setMoreMenuAnchorEl(null);
                    setDeleteDialogOpen(true);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <Delete fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText>Delete election</ListItemText>
                </MenuItem>
              )}
            </Menu>
            {prepLink && 
            <a href={prepLink} target='_blank'>
              <IconButton>
                <Preview />
              </IconButton>
            </a>}
          </>
        }
      />
      <Typography style={{ marginBottom: '24px', marginTop: '36px' }} variant={'h1'}>Welcome{displayName ? `, ${displayName}` : ''}!</Typography>
      <Content>
        <ElectionSummaryTile fullCoverageVisible={canViewDetailedElectionData ? 1 : 0}>
          <div
            style={{
              padding: '8px 8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'space-between',
              height: 'calc(100% - 8px * 2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' , alignItems: 'center' }}>
              <Typography variant={'body1'}>{election?.name}</Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ElectionStatusTag election={election} />
                {editElectionStatusPermission && <IconButton size='small' onClick={() => setIsEditingElectionStatus(true)} >
                  <Edit fontSize="small"/>
                </IconButton>}
                <Modal
                  open={isEditingElectionStatus}
                  onClose={() => setIsEditingElectionStatus(false)}
                >
                  <EditElectionStatusModal
                    election={election}
                    onClose={(election) => handleEditElectionStatusModalClose(election)}
                  />
                </Modal>
              </div>
            </div>
            <Typography variant={'h2'} style={{fontSize: '38px'}}>{moment.utc(election?.date).format('MMMM Do, YYYY')}</Typography>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant={'body1'}>{electionTypeWritten(election)}</Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant={'body1'}>{upcomingReminders?.length ?? 0} upcoming reminders</Typography>
                <IconButton size='small' style={{ padding: 0 }} onClick={() => setOpenReminderModal(true)}>
                  <CalendarMonth fontSize='small'/>
                </IconButton>
              </div>
            </div>
              
          </div>
        </ElectionSummaryTile>
        {
          canViewDetailedElectionData &&
          <ProgressSummaryTile fullCoverageVisible={canViewDetailedElectionData ? 1 : 0}>
            <div
              style={{
                padding: '0px 8px 8px',
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
                height: 'calc(100% - 8px * 2)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px'}}>
                <Typography variant={'body1'} style={{ fontWeight: 'bold' }}>Progress</Typography>
                <Link to={`/elections/${electionKey}/progress`}>
                  <Typography variant={'body2'}><u>Details</u></Typography>
                </Link>
                <Link to={`/elections/${electionKey}/geodistricts`}>
                  <Typography variant={'body2'}><u>Geodistricts</u></Typography>
                </Link>
                {canAdjustPriorityLevels && (
                  <>
                    <IconButton
                      size="small"
                      onClick={(event) => setProgressMoreMenuAnchorEl(event.currentTarget)}
                      style={{ marginLeft: 'auto' }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={progressMoreMenuAnchorEl}
                      open={Boolean(progressMoreMenuAnchorEl)}
                      onClose={() => setProgressMoreMenuAnchorEl(null)}
                    >
                      <MenuItem
                        onClick={() => {
                          setProgressMoreMenuAnchorEl(null);
                          setAdjustPriorityLevelsModalOpen(true);
                        }}
                      >
                        Adjust priority levels
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: '22px', alignItems: 'center', justifyContent: 'space-around' }}>
                <CircularProgressBar
                  percentage={Boolean(electionImplementation?.aggregatedInformation?.numScoutingDistricts)
                    ? Math.round((electionImplementation?.aggregatedInformation?.numScoutingDistrictsCompleted / electionImplementation?.aggregatedInformation?.numScoutingDistricts) * 100)
                    : 0}
                  subtext={'scouting complete'}
                  dataTip={
                    `${typeof(electionImplementation?.aggregatedInformation?.numScoutingDistrictsCompleted) === 'undefined' 
                    ? '--' 
                    : electionImplementation?.aggregatedInformation?.numScoutingDistrictsCompleted
                  } of ${
                    typeof(electionImplementation?.aggregatedInformation?.numScoutingDistricts) === 'undefined'
                    ? '--'
                    : electionImplementation?.aggregatedInformation?.numScoutingDistricts
                  } districts scouted`}
                />
                <div
                  onMouseEnter={(e) => setCandidatesPopoverAnchorEl(e.currentTarget)}
                  onMouseLeave={() => setCandidatesPopoverAnchorEl(null)}
                >
                  <CircularProgressBar
                    percentage={Boolean(electionImplementation?.aggregatedInformation?.numCandidates)
                      ? Math.round((electionImplementation?.aggregatedInformation?.numCandidatesCompleted / electionImplementation?.aggregatedInformation?.numCandidates) * 100)
                      : 0}
                    subtext={'candidates complete'}
                    dataTip={
                      `${typeof(electionImplementation?.aggregatedInformation?.numCandidatesCompleted) === 'undefined' 
                      ? '--' 
                      : electionImplementation?.aggregatedInformation?.numCandidatesCompleted
                    } of ${
                      typeof(electionImplementation?.aggregatedInformation?.numCandidates) === 'undefined'
                      ? '--'
                      : electionImplementation?.aggregatedInformation?.numCandidates
                    } candidates complete`}
                  />
                  <Popover
                    open={Boolean(candidatesPopoverAnchorEl)}
                    anchorEl={candidatesPopoverAnchorEl}
                    onClose={() => setCandidatesPopoverAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    disableRestoreFocus
                    onMouseEnter={() => {}} // Keep popover open when hovering over it
                    onMouseLeave={() => setCandidatesPopoverAnchorEl(null)}
                  >
                    <div
                      onMouseEnter={() => {}} // Keep popover open
                      onMouseLeave={() => setCandidatesPopoverAnchorEl(null)}
                    >
                      <AnalyticsPopoverContent
                        analyticsData={electionImplementation?.aggregatedInformation}
                        type="candidates"
                      />
                    </div>
                  </Popover>
                </div>
                <div
                  onMouseEnter={(e) => setRacesPopoverAnchorEl(e.currentTarget)}
                  onMouseLeave={() => setRacesPopoverAnchorEl(null)}
                >
                  <CircularProgressBar
                    percentage={Boolean(electionImplementation?.aggregatedInformation?.numRaces)
                      ? Math.round((electionImplementation?.aggregatedInformation?.numRacesCompleted / electionImplementation?.aggregatedInformation?.numRaces) * 100)
                      : 0}
                    subtext={'races complete'}
                    dataTip={
                      `${typeof(electionImplementation?.aggregatedInformation?.numRacesCompleted) === 'undefined' 
                      ? '--' 
                      : electionImplementation?.aggregatedInformation?.numRacesCompleted
                    } of ${
                      typeof(electionImplementation?.aggregatedInformation?.numRaces) === 'undefined'
                      ? '--'
                      : electionImplementation?.aggregatedInformation?.numRaces
                    } complete`}
                  />
                  <Popover
                    open={Boolean(racesPopoverAnchorEl)}
                    anchorEl={racesPopoverAnchorEl}
                    onClose={() => setRacesPopoverAnchorEl(null)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    disableRestoreFocus
                    onMouseEnter={() => {}} // Keep popover open when hovering over it
                    onMouseLeave={() => setRacesPopoverAnchorEl(null)}
                  >
                    <div
                      onMouseEnter={() => {}} // Keep popover open
                      onMouseLeave={() => setRacesPopoverAnchorEl(null)}
                    >
                      <AnalyticsPopoverContent
                        analyticsData={electionImplementation?.aggregatedInformation}
                        type="races"
                      />
                    </div>
                  </Popover>
                </div>
                <CircularProgressBar
                  percentage={Boolean(electionImplementation?.aggregatedInformation?.numMeasures)
                    ? Math.round((electionImplementation?.aggregatedInformation?.numMeasuresCompleted / electionImplementation?.aggregatedInformation?.numMeasures) * 100)
                    : 0}
                  subtext={'measures complete'}
                  dataTip={
                    `${typeof(electionImplementation?.aggregatedInformation?.numMeasuresCompleted) === 'undefined' 
                    ? '--' 
                    : electionImplementation?.aggregatedInformation?.numMeasuresCompleted
                  } of ${
                    typeof(electionImplementation?.aggregatedInformation?.numMeasures) === 'undefined'
                    ? '--'
                    : electionImplementation?.aggregatedInformation?.numMeasures
                  } complete`}
                />
              </div>
            </div>
          </ProgressSummaryTile>
        }
        <RacesTile onClick={{ to: `/elections/${electionKey}/races`}} fullCoverageVisible={canViewDetailedElectionData ? 1 : 0}>
          <div
            style={{
              padding: '8px 8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'space-between',
              height: 'calc(100% - 8px * 2)',
            }}
          >
            <Typography variant={'body1'}>Races</Typography>
            <Typography variant={'h2'} style={{fontSize: '38px'}}>{electionImplementation?.aggregatedInformation?.numRaces ?? 0}</Typography>
            <Typography variant={'body1'}><u>View races</u></Typography>
          </div>
        </RacesTile>
        <MeasuresTile onClick={{ to: `/elections/${electionKey}/measures`}} fullCoverageVisible={canViewDetailedElectionData ? 1 : 0}>
          <div
            style={{
              padding: '8px 8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'space-between',
              height: 'calc(100% - 8px * 2)',
            }}
          >
            <Typography variant={'body1'}>Referendums</Typography>
            <Typography variant={'h2'} style={{fontSize: '38px'}}>{electionImplementation?.aggregatedInformation?.numMeasures ?? 0}</Typography>
            <Typography variant={'body1'}><u>View referendums</u></Typography>
          </div>
        </MeasuresTile>
        {
          canViewDetailedElectionData &&
          <CoveragePlanTile style={{}} fullCoverageVisible={canViewDetailedElectionData ? 1 : 0}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px'}}>
              <Typography variant={'body1'} style={{ fontWeight: 'bold' }}>Coverage plan</Typography>
              <Link to={`/elections/${electionKey}/coverage-plan`}>
                <Typography variant={'body2'}><u>Details</u></Typography>
              </Link>
            </div>
            <div style={{ height: 'calc(176px * 2 - 52px)', width: '100%' }}>
              {
                Boolean(electionImplementation)
                ? <CoveragePlanMap
                  electionImplementation={electionImplementation}
                />
                : <Typography variant={'body1'}>No coverage plan set yet for this election.</Typography>
              }
            </div>
          </CoveragePlanTile>
        }
        <DatesTile>
          <div
            style={{
              padding: '8px 8px',
              height: 'calc(100% - 8px * 2)',
              display: 'grid',
              gridGap: '36px',
              gridTemplateColumns: '1fr 1fr',
              position: 'relative'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column'}}>
              <Typography variant={'body1'} style={{ fontWeight: 'bold' }}>Election dates</Typography>
              <ElectionTimeline
                election={election}
                datesPassed='show'
                subtextEnabled={false}
                mode='calendar'
              />
            </div>
            {
              canViewDetailedElectionData
              ? <div style={{ display: 'flex', flexDirection: 'column'}}>
                  <Typography variant={'body1'} style={{ fontWeight: 'bold' }}>Project dates</Typography>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: `auto 1fr auto`,
                    gridGap: `8px 8px`,
                    gridTemplateRows: 'repeat(5, auto)',
                    alignItems: 'flex-start'
                  }}>
                    <VotingDate
                      dateTitle='Qualifying ends'
                      dateWritten={election?.qualifyingDate ? moment.utc(election?.qualifyingDate).format('MMM D') : '--'}
                    />
                    <VotingDate
                      dateTitle='Candidate profiling starts'
                      dateWritten={electionImplementation?.profilingStart ? moment.utc(electionImplementation?.profilingStart).format('MMM D') : '--'}
                    />
                    <VotingDate
                      dateTitle='Race scouting starts'
                      dateWritten={electionImplementation?.raceScoutingStartDate ? moment.utc(electionImplementation?.raceScoutingStartDate).format('MMM D') : '--'}
                    />
                    <VotingDate
                      dateTitle='Measure scouting starts'
                      dateWritten={electionImplementation?.measureScoutingStartDate ? moment.utc(electionImplementation?.measureScoutingStartDate).format('MMM D') : '--'}
                    />
                    <VotingDate
                      dateTitle='Go live'
                      dateWritten={electionImplementation?.goLiveDate ? moment.utc(electionImplementation?.goLiveDate).format('MMM D') : '--'}
                    />
                  </div>
                </div>
              : <div/>
            }
            {
              datesEditPermission &&
              <Link to={`/elections/${electionKey}/dates/edit`}  style={{ position:'absolute', right: 0, top: 0}}>
                <IconButton size='small'>
                  <Edit fontSize='small' />
                </IconButton>
              </Link>
            }
          </div>
        </DatesTile>
        <OfficeListTile style={{}} fullCoverageVisible={canViewDetailedElectionData ? 1 : 0}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px'}}>
            <Typography variant={'body1'} style={{ fontWeight: 'bold' }}>What's on the ballot</Typography>
            <Link to={`/offices?election=${electionKey}`}>
              <Typography variant={'body2'}><u>Details</u></Typography>
            </Link>
            {datesEditPermission && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setEditBallotOverviewModalOpen(true)}
                style={{ marginLeft: 'auto' }}
              >
                Edit overview
              </Button>
            )}
          </div>
          <div style={{ height: 'calc(176px * 2 - 52px)', width: '100%' }}>
            <OfficesList
              aggregatedInformation={electionImplementation?.aggregatedInformation}
              loading={!Boolean(electionImplementation)}
              canViewDetailedElectionData={canViewDetailedElectionData}
              ballotOverview={election?.ballotOverview}
            />
          </div>
        </OfficeListTile>
      </Content>
      <Modal open={openReminderModal} onClose={() => setOpenReminderModal(false)}>
        <ModalInner onClose={() => setOpenReminderModal(false)}>
          <ElectionReminderSchedule
            reminders={election?.reminderSchedule}
            election={election}
            onClose={() => setOpenReminderModal(false)}
          />
        </ModalInner>
      </Modal>
      <Modal open={editBallotOverviewModalOpen} onClose={() => setEditBallotOverviewModalOpen(false)}>
        <ModalInner
          onClose={() => setEditBallotOverviewModalOpen(false)}
          style={{ overflowY: 'visible', overflowX: 'visible' }}
        >
          <EditBallotOverview
            election={election}
            saving={savingBallotOverview}
            onSave={async (ballotOverview) => {
              setSavingBallotOverview(true);
              try {
                await feathers.getService('elections').patch(electionKey, {
                  ballotOverview,
                  setup: {
                    ...(election?.setup || {}),
                    ballotOverview: true,
                  },
                });
                await refreshElectionData();
                setEditBallotOverviewModalOpen(false);
              } finally {
                setSavingBallotOverview(false);
              }
            }}
          />
        </ModalInner>
      </Modal>
      <AdjustPriorityLevelsModal
        open={adjustPriorityLevelsModalOpen}
        onClose={() => setAdjustPriorityLevelsModalOpen(false)}
        electionKey={electionKey}
        electionImplementation={electionImplementation}
        feathers={feathers}
        onSaved={refreshElectionData}
      />
      <LinkElectionPrecedentModal
        open={linkPrecedentModalOpen}
        onClose={() => setLinkPrecedentModalOpen(false)}
        election={election}
        feathers={feathers}
        onSaved={refreshElectionData}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to delete this election?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action cannot be undone. The election "{election?.name}" will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteElection}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  )
}


const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  min-height: calc(100% - 24px * 2);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const DashboardTile = ({style, ...props}) => <Tile {...({
  ...(props || {}),
  shadowEnabled: true,
  chevronEnabled: false,
  style: {
    ...(style || {}),
  }
})}/>

/*
   Defining our grid for the tiles
   width between 700 and 1000 will be 3 columns
   width >= 1000 will be 4 columns
* */

const range2 = `(min-width: 700px) and (max-width: 1000px)`
const range3 = `(min-width: 1000px)`

const Content = styled.div`
  width: 100%;
  display: grid;
  grid-gap: 16px;
  align-items: stretch;
  grid-template-rows: repeat(4, 176px);

  @media${range2} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media${range3} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
`

const ElectionSummaryTile = styled(DashboardTile)`
  @media${range2} {
    grid-row: 1 / span 1;
    grid-column: 1 / span 2;
  }

  @media${range3} {
    grid-row: 1 / span 1;
    grid-column: 1 / span 2;
  }
`

const ProgressSummaryTile = styled(DashboardTile)`
  @media${range2} {
    grid-column: 2 / span 2;
    grid-row: 5 / span 1;
  }

  @media${range3} {
    grid-row: 2 / span 1;
    grid-column: 3 / span 2;
  }
`

const RacesTile = styled(DashboardTile)`

  ${props => props.fullCoverageVisible ?
    `
    @media${range2} {
      grid-row: 1 / span 1;
      grid-column: 3 / span 1;
    }

    @media${range3} {
      grid-row: 3 / span 1;
      grid-column: 3 / span 1;
    }
  ` : `
    @media${range2} {
      grid-row: 1 / span 1;
      grid-column: 3 / span 1;
    }

    @media${range3} {
      grid-row: 2 / span 1;
      grid-column: 3 / span 1;
    }
  `}  
`

const MeasuresTile = styled(DashboardTile)`
  ${props => props.fullCoverageVisible ?
    `
    @media${range2} {
      grid-row: 2 / span 1;
      grid-column: 3 / span 1;
    }
  
    @media${range3} {
      grid-row: 3 / span 1;
      grid-column: 4 / span 1;
    }
  ` : `
    @media${range2} {
      grid-row: 2 / span 1;
      grid-column: 3 / span 1;
    }

    @media${range3} {
      grid-row: 2 / span 1;
      grid-column: 4 / span 1;
    }
  `}  
`

const CoveragePlanTile = styled(DashboardTile)`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  @media${range2} {
    grid-row: 3 / span 2;
    grid-column: 1 / span 3;
  }
  overflow-y: auto;
  @media${range3} {
    grid-row: 2 / span 2;
    grid-column: 1 / span 2;
  }
`

const OfficeListTile = styled(DashboardTile)`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  ${props => props.fullCoverageVisible ?
  `
    @media${range2} {
      grid-row: 6 / span 2;
      grid-column: 1 / span 3;
    }
    @media${range3} {
      grid-row: 4 / span 2;
      grid-column: 1 / span 2;
    }
  `: `
    @media${range2} {
      grid-row: 3 / span 2;
      grid-column: 1 / span 2;
    }
    @media${range3} {
      grid-row: 2 / span 2;
      grid-column: 1 / span 2;
    }
  `
  }
  
  overflow-y: auto;
  
`

const CandidateListsTile = styled(DashboardTile)`
  @media${range2} {
    grid-row: 7 / span 1;
    grid-column: 1 / span 1;
  }
  @media${range3} {
    grid-row: 5 / span 1;
    grid-column: 1 / span 1;
  }
`

const DatesTile = styled(DashboardTile)`
  @media${range2} {
    grid-row: 2 / span 1;
    grid-column: 1 / span 2;
  }
  @media${range3} {
    grid-column: 3 / span 2;
    grid-row: 1 / span 1;
  }

  overflow-y: auto;
`

const LoadingTile = styled.div`
  width: 100%;
  align-self: center;
  justify-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;


const CoverageProgressBar = ({ numerator, denominator, name, style, size }) => (
  <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'stretch', ...(style || {}) }}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px', justifyContent: 'space-between' }}>
      <Typography variant={'body1'} style={size === 'small' ? { fontSize: '14px'} : { fontWeight: 'bold' }}>{numerator} out of {denominator} {name} complete</Typography>
      <Typography variant={'body1'} style={size === 'small' ? { fontSize: '14px'} : { fontWeight: 'bold' }}>{denominator === 0 ? 100 : Math.round(numerator / denominator * 100)}%</Typography>
    </div>
    <ProgressBar percentage={denominator === 0 ? 100 : Math.round(numerator / denominator * 100)} height={size === 'small' ? 4 : 12} backgroundColor={'#D8D8D8'}/>
  </div>
)


export default ElectionDetail;
